import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { clientAPI } from "@/services/api";
import {
  AlertTriangle,
  Check,
  Copy,
  Eye,
  EyeOff,
  Key,
  Link,
  Loader2,
  RefreshCw,
  Save,
  Webhook,
} from "lucide-react";
import {
  TailAdminDetailRow,
  TailAdminFormSection,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const maskKey = (key) => (key ? `${key.slice(0, 8)}...${key.slice(-8)}` : "No API key available");

const ClientSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [apiEndpoint, setApiEndpoint] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await clientAPI.getSettings();
      setSettings(res.data || {});
      setApiEndpoint(res.data?.api_endpoint || "");
      setWebhookUrl(res.data?.webhook_url || "");
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleCopyApiKey = () => {
    if (!settings?.api_key) return;
    navigator.clipboard.writeText(settings.api_key);
    setCopied(true);
    toast.success("API key copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerateKey = async () => {
    setRegenerating(true);
    try {
      const res = await clientAPI.regenerateAPIKey();
      setSettings((current) => ({ ...(current || {}), api_key: res.data?.api_key }));
      toast.success("API key regenerated");
      setShowRegenerateDialog(false);
    } catch {
      toast.error("Failed to regenerate key");
    } finally {
      setRegenerating(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await clientAPI.updateAPISettings(apiEndpoint || null, webhookUrl || null);
      toast.success("Settings saved");
      fetchSettings();
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <TailAdminPageHeader
        eyebrow="Integrations"
        title="API & Integration Settings"
        description="Manage API credentials, enrollment sync endpoint, and webhook delivery for your active product."
        actions={
          <Button onClick={handleSaveSettings} disabled={saving || loading} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Settings
          </Button>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-6">
          <TailAdminFormSection
            icon={Key}
            title="API Key"
            description="Use this key to authenticate product API requests from your integration."
          >
            {loading ? (
              <Skeleton className="h-11 w-full" />
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="relative flex-1">
                    <Input
                      value={showApiKey ? settings?.api_key || "" : maskKey(settings?.api_key)}
                      readOnly
                      className="h-11 pr-12 font-mono text-sm"
                      data-testid="api-key-input"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2"
                      onClick={() => setShowApiKey((value) => !value)}
                      data-testid="toggle-api-key-btn"
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button variant="outline" onClick={handleCopyApiKey} data-testid="copy-api-key-btn">
                    {copied ? <Check className="h-4 w-4 text-[#039855]" /> : <Copy className="h-4 w-4" />}
                    Copy
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setShowRegenerateDialog(true)} data-testid="regenerate-key-btn">
                  <RefreshCw className="h-4 w-4" />
                  Regenerate Key
                </Button>
              </div>
            )}
          </TailAdminFormSection>

          <TailAdminFormSection
            icon={Link}
            title="API Endpoint"
            description="Proovam periodically calls this endpoint to verify subscription and enrollment state."
          >
            {loading ? (
              <Skeleton className="h-11 w-full" />
            ) : (
              <div className="space-y-2">
                <Label htmlFor="api-endpoint">Endpoint URL</Label>
                <Input
                  id="api-endpoint"
                  type="url"
                  placeholder="https://api.yourcompany.com/enrollments"
                  value={apiEndpoint}
                  onChange={(event) => setApiEndpoint(event.target.value)}
                  data-testid="api-endpoint-input"
                />
                <p className="text-xs text-gray-500">Expected contract: GET /check-enrollment?phone_hash=...</p>
              </div>
            )}
          </TailAdminFormSection>

          <TailAdminFormSection
            icon={Webhook}
            title="Webhook URL"
            description="Receive real-time POST notifications when enrollment events change."
          >
            {loading ? (
              <Skeleton className="h-11 w-full" />
            ) : (
              <div className="space-y-2">
                <Label htmlFor="webhook-url">Webhook URL</Label>
                <Input
                  id="webhook-url"
                  type="url"
                  placeholder="https://yourcompany.com/webhooks/proovam"
                  value={webhookUrl}
                  onChange={(event) => setWebhookUrl(event.target.value)}
                  data-testid="webhook-url-input"
                />
                <p className="text-xs text-gray-500">Leave blank if you only want to use periodic sync.</p>
              </div>
            )}
          </TailAdminFormSection>
        </div>

        <TailAdminPanel title="Integration status" description="Current active-product configuration summary.">
          <div className="space-y-3">
            <TailAdminDetailRow
              label="API key"
              helper="Credential available for campaign integration"
              value={<TailAdminStatusBadge tone={settings?.api_key ? "success" : "warning"}>{settings?.api_key ? "Ready" : "Missing"}</TailAdminStatusBadge>}
            />
            <TailAdminDetailRow
              label="Sync endpoint"
              helper={apiEndpoint || "No endpoint configured"}
              value={<TailAdminStatusBadge tone={apiEndpoint ? "success" : "neutral"}>{apiEndpoint ? "Configured" : "Not set"}</TailAdminStatusBadge>}
            />
            <TailAdminDetailRow
              label="Webhook"
              helper={webhookUrl || "No webhook configured"}
              value={<TailAdminStatusBadge tone={webhookUrl ? "success" : "neutral"}>{webhookUrl ? "Configured" : "Optional"}</TailAdminStatusBadge>}
            />
          </div>
        </TailAdminPanel>
      </div>

      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#dc6803]" />
              Regenerate API Key?
            </DialogTitle>
            <DialogDescription>
              This invalidates the current key. Existing integrations using the old key will stop working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRegenerateKey} disabled={regenerating} data-testid="confirm-regenerate-btn">
              {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientSettings;
